import { ReactNode } from 'react';
import { Spinner } from '@servicetitan/design-system';
import { useVirtualAgentFeatureGate } from '@api/hooks/useVirtualAgentFeatureGate';
import RedirectForbidden from '@app/permissions/RedirectForbidden';
import { RequirePermissions } from '@app/permissions';
import { permissions } from '@app/permissions/permissions';
import { FeatureErrorBoundary } from '@components/ErrorBoundary';

interface VoiceAgentDashboardRouteGuardProps {
  children: ReactNode;
}

/**
 * Route guard for the Voice Agent Dashboard.
 * Shows a spinner while feature flags/gates load, redirects to forbidden
 * if the feature is not enabled or the user lacks the required permission,
 * or renders children when authorized.
 */
const VoiceAgentDashboardRouteGuard = ({
  children,
}: VoiceAgentDashboardRouteGuardProps) => {
  const virtualAgentFeatures = useVirtualAgentFeatureGate();

  if (virtualAgentFeatures.loading) {
    return <Spinner />;
  }

  if (!virtualAgentFeatures.reporting) {
    return <RedirectForbidden />;
  }

  return (
    <RequirePermissions
      permissions={[permissions.admin.REPORTS.VOICE_AGENT_DASHBOARD_VIEW]}
      permissionsMatch='all'
      unavailable={<RedirectForbidden />}
    >
      <FeatureErrorBoundary featureName='Voice Agent Dashboard'>
        {children}
      </FeatureErrorBoundary>
    </RequirePermissions>
  );
};

export default VoiceAgentDashboardRouteGuard;
